import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Studio = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const equipment = {
    monitoringStereo: {
      title: 'MONITORING STEREO',
      items: ['FOCAL TWIN BE6', 'YAMAHA HS8'],
    },
    monitoringAtmos: {
      title: 'MONITORING DOLBY ATMOS',
      items: ['GENELEC 8340 X 7', 'GENELEC 8330 X 4', 'GENELEC 7380'],
    },
    software: {
      title: 'SOFTWARE',
      items: ['PRO TOOLS STUDIO', 'ABLETON LIVE 12', 'LOGIC PRO X', 'DOLBY ATMOS MASTERING SUITE'],
    },
    hardware: {
      title: 'HARDWARE',
      items: [
        'MACSTUDIO M2 ULTRA',
        'MACMINI 6CORE I7',
        'UNIVERSAL AUDIO APOLLO 6X',
        'UNIVERSAL AUDIO APOLLO 16',
        'UNIVERSAL AUDIO APOLLO TWIN',
        'UNIVERSAL AUDIO SATELLITE DSP',
        'APOGEE ROSETTA 200',
        'MONITOR CONTROLLER LA-126',
        'AUDIOXL X LIVIO ARGENTINI',
        'AUDIOSCOPE',
      ],
    },
    synths: {
      title: 'KEYBOARD, SYNTH & DRUM',
      items: [
        'ROLAND JUNO 106',
        'MOOG SUB37',
        'ACCESS VIRUS INDIGO',
        'KORG MINILOGUE',
        'STUDIOLOGIC SLEDGE',
        'EURORACK MODULAR SYSTEM',
        'KORG MS2000R',
        'MPC 1000',
        'KORG ELECTRIBE',
        'KORG MICROKORG XL',
      ],
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="studio"
      data-testid="studio-section"
      ref={ref}
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-24"
        >
          <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
            Studio
          </span>
          <h2
            className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
            data-testid="studio-heading"
          >
            TECHNICAL
            <br />
            EQUIPMENT
          </h2>
        </motion.div>

        {/* Studio Images */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-20 md:mb-32"
        >
          <div className="relative overflow-hidden group aspect-video">
            <img
              src="https://www.audioxlmilano.com/assets/studio2.jpg"
              alt="AudioXL Studio Detail"
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
              data-testid="studio-image-1"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-all duration-500" />
          </div>
          <div className="relative overflow-hidden group aspect-video">
            <img
              src="/assets/studio-b.jpg"
              alt="AudioXL Studio"
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
              data-testid="studio-image-2"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-all duration-500" />
          </div>
        </motion.div>

        {/* Equipment Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16"
        >
          {Object.entries(equipment).map(([key, category]) => (
            <motion.div
              key={key}
              variants={itemVariants}
              data-testid={`equipment-${key}`}
            >
              <h3 className="font-heading font-bold text-xs tracking-[0.2em] text-white/40 mb-6 pb-4 border-b border-white/10">
                {category.title}
              </h3>
              <ul className="space-y-3">
                {category.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="font-body text-sm text-white/60">
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Studio;
